import { ProseMirror } from "@progress/kendo-react-editor";
import { handleEditorError } from "../hooks";

/**
 * ProseMirror AutoLink Plugin for Kendo Editor
 * Provides cursor-safe URL autolinking using ProseMirror's transaction system
 */

const { Plugin, PluginKey } = ProseMirror;

export interface AutolinkPluginOptions {
  urlRegex?: RegExp;
  triggerChars?: string[];
}

// Centralized URL regex pattern - handles query parameters directly after domain
const URL_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:[\w-]+\.)*[\w-]+\.(?:com|org|net|gov|edu|mil|int|co|io|cloud|app)(?:\.[\w]{2,4})?(?:\/[^\s]*|\?[^\s]*)?(?=\s|[.,;!])/gi;

// Only whitespace characters to prevent premature linking during typing
const TRIGGER_CHARS = [" ", "\n", "\t"];

const DEFAULT_OPTIONS: Required<AutolinkPluginOptions> = {
  urlRegex: URL_REGEX,
  triggerChars: TRIGGER_CHARS
};

export const autolinkPluginKey = new PluginKey("autolink");

// Export constants for plugin manager to eliminate duplication
export { URL_REGEX, TRIGGER_CHARS };

const findUrlsInText = (
  text: string,
  basePos: number,
  regex: RegExp
): Array<{ from: number; to: number; url: string }> => {
  const matches: Array<{ from: number; to: number; url: string }> = [];
  let match;

  regex.lastIndex = 0;

  while ((match = regex.exec(text)) !== null) {
    const matchText = match[0];
    const from = basePos + match.index;
    const to = from + matchText.length;

    let url = matchText.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    matches.push({ from, to, url });

    if (match.index === regex.lastIndex) {
      regex.lastIndex++;
    }
  }

  return matches;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isInsideLink = (doc: any, pos: number): boolean => {
  try {
    const $pos = doc.resolve(pos);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return $pos.marks().some((mark: any) => mark.type.name === "link");
  } catch {
    return false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const shouldAutolinkUrl = (doc: any, from: number, to: number): boolean => {
  try {
    if (isInsideLink(doc, from) || isInsideLink(doc, to - 1)) {
      return false;
    }

    const $from = doc.resolve(from);
    const urlText = $from.parent.textContent.slice(
      from - $from.start(),
      to - $from.start()
    );

    const completeTldPattern =
      /\.(com|org|net|gov|edu|mil|int|io|cloud|app|co\.uk|co\.jp|co\.au)(?:\/.*|\?.*)?$/;
    if (!completeTldPattern.test(urlText.toLowerCase())) {
      return false;
    }

    // More restrictive check - only allow linking if followed by whitespace or at end of document
    if (to < doc.content.size) {
      const $after = doc.resolve(to);
      const afterText = $after.parent.textContent.slice($after.parentOffset);
      const nextChar = afterText.charAt(0);

      // Only allow linking if followed by actual whitespace (space, tab, newline)
      if (!/[\s\n\t]/.test(nextChar)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    handleEditorError(error as Error, "autolink URL validation");
    return false;
  }
};

export const createAutolinkPlugin = (
  options: AutolinkPluginOptions = {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processAutolinking = (newState: any) => {
    try {
      const selection = newState.selection;
      const cursorPos = selection.$from.pos;

      const searchStart = Math.max(0, cursorPos - 200);
      const searchEnd = Math.min(newState.doc.content.size, cursorPos + 100);

      const urls: Array<{ from: number; to: number; url: string }> = [];

      newState.doc.nodesBetween(
        searchStart,
        searchEnd,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node: any, pos: number) => {
          if (node.isText) {
            const matches = findUrlsInText(
              node.textContent,
              pos,
              new RegExp(opts.urlRegex.source, "gi")
            );

            matches.forEach(match => {
              if (shouldAutolinkUrl(newState.doc, match.from, match.to)) {
                urls.push(match);
              }
            });
          }
          return true;
        }
      );

      if (urls.length === 0) {
        return null;
      }

      const tr = newState.tr;
      let hasChanges = false;

      urls.reverse().forEach(({ from, to, url }) => {
        try {
          const linkMark = newState.schema.marks.link.create({
            href: url,
            target: "_blank",
            rel: "noopener noreferrer"
          });

          tr.addMark(from, to, linkMark);
          hasChanges = true;
        } catch (error) {
          handleEditorError(error as Error, `applying autolink for ${url}`);
        }
      });

      if (hasChanges) {
        tr.setMeta("autolink-applied", true);
        return tr;
      }

      return null;
    } catch (error) {
      handleEditorError(error as Error, "autolink processing");
      return null;
    }
  };

  return new Plugin({
    key: autolinkPluginKey,

    appendTransaction(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transactions: readonly any[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oldState: any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      newState: any
    ) {
      try {
        const hasUserInput = transactions.some(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (tr: any) =>
            tr.docChanged &&
            !tr.getMeta("autolink-applied") &&
            !tr.getMeta("paste")
        );

        if (!hasUserInput) {
          return null;
        }

        const lastTr = transactions[transactions.length - 1];
        if (lastTr?.steps?.length > 0) {
          const lastStep = lastTr.steps[lastTr.steps.length - 1];

          if (lastStep.slice?.content) {
            const insertedText = lastStep.slice.content.textContent || "";
            const hasTriggerChar = opts.triggerChars.some(char =>
              insertedText.includes(char)
            );

            if (
              hasTriggerChar ||
              insertedText.match(/\.(com|org|net|gov|edu|io)($|\s)/)
            ) {
              return processAutolinking(newState);
            }
          }
        }

        const selection = newState.selection;
        const cursorPos = selection.$from.pos;
        const searchStart = Math.max(0, cursorPos - 50);
        const searchEnd = Math.min(newState.doc.content.size, cursorPos + 10);

        let foundUrls = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newState.doc.nodesBetween(searchStart, searchEnd, (node: any) => {
          if (node.isText && opts.urlRegex.test(node.textContent)) {
            foundUrls = true;
            return false;
          }
          return true;
        });

        if (foundUrls) {
          return processAutolinking(newState);
        }

        return null;
      } catch (error) {
        handleEditorError(error as Error, "autolink plugin appendTransaction");
        return null;
      }
    },

    props: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleTextInput(view: any, from: number, to: number, text: string) {
        try {
          const hasTriggerChar = opts.triggerChars.some(char =>
            text.includes(char)
          );

          if (!hasTriggerChar) {
            return false;
          }

          // Immediate processing instead of setTimeout
          const state = view.state;
          const transaction = processAutolinking(state);

          if (transaction) {
            view.dispatch(transaction);

            // Trigger Kendo Editor's onChange after dispatch
            setTimeout(() => {
              try {
                // Try to trigger Kendo's internal update mechanism
                if (view.dom && view.dom.dispatchEvent) {
                  const inputEvent = new Event("input", { bubbles: true });
                  view.dom.dispatchEvent(inputEvent);
                }

                // Alternative: trigger Kendo Editor's internal change detection
                if (view.updateState) {
                  view.updateState(view.state);
                }
              } catch (syncError) {
                handleEditorError(
                  syncError as Error,
                  "syncing Kendo Editor state"
                );
              }
            }, 10); // Very short delay to let ProseMirror settle

            return true; // Indicate we handled the input
          }

          return false;
        } catch (error) {
          handleEditorError(error as Error, "autolink text input handling");
          return false;
        }
      }
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAutolinkPluginActive = (state: any): boolean => {
  return autolinkPluginKey.getState(state) !== undefined;
};
